import { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import { definitions } from '@luxuryescapes/contract-svc-promo';

import { PromoMeta, createOrUpdatePromoDisplayConfigRuleJson } from '~/services/PromoService';

type RowData = definitions['RuleJson']['displayOptions'][0] & {
  id: number;
  isNew?: boolean;
};

type UsePromoDisplayConfigRulesProps = {
  ruleJSON: definitions['RuleJson'];
  promoDisplayConfigId: string;
  promoMeta: PromoMeta;
  onRuleJsonUpdate?: (updatedRuleJson: definitions['RuleJson']) => void;
};

export const usePromoDisplayConfigRules = ({
  ruleJSON,
  promoDisplayConfigId,
  onRuleJsonUpdate,
  promoMeta,
}: UsePromoDisplayConfigRulesProps) => {
  const { enqueueSnackbar } = useSnackbar();

  // State management
  const [localRuleJSON, setLocalRuleJSON] = useState<definitions['RuleJson'] | null>(ruleJSON || null);
  const [rows, setRows] = useState<RowData[]>(
    ruleJSON?.displayOptions?.map((row, index) => ({ ...row, id: index })) || [],
  );
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedRuleJSON, setLastSavedRuleJSON] = useState<definitions['RuleJson'] | null>(ruleJSON || null);

  const hasUnsavedChanges = localRuleJSON && JSON.stringify(localRuleJSON) !== JSON.stringify(lastSavedRuleJSON);

  useEffect(() => {
    if (ruleJSON) {
      setLocalRuleJSON(ruleJSON);
      setLastSavedRuleJSON(ruleJSON);
      setRows(ruleJSON.displayOptions?.map((row, index) => ({ ...row, id: index })) || []);
    }
  }, [ruleJSON]);

  // Helper to update displayOptionKey in localRuleJSON
  const setDisplayOptionKey = useCallback(
    (value: string) => {
      if (localRuleJSON) {
        const updatedRuleJSON = {
          ...localRuleJSON,
          displayOptionKey: value,
        };
        setLocalRuleJSON(updatedRuleJSON as definitions['RuleJson']);

        // Update the parent form's ruleJSON state to keep it in sync
        onRuleJsonUpdate?.(updatedRuleJSON as definitions['RuleJson']);
      }
    },
    [localRuleJSON, onRuleJsonUpdate],
  );

  const updateLocalRuleJSON = useCallback(
    (updatedRows: RowData[]) => {
      const displayOptions = updatedRows.map(({ id, isNew, ...row }) => row);
      const updatedRuleJSON = {
        ...(localRuleJSON || { conditions: [] }),
        displayOptions,
      };
      setLocalRuleJSON(updatedRuleJSON as definitions['RuleJson']);
    },
    [localRuleJSON],
  );

  // Update conditions for a specific row
  const updateRowConditions = useCallback(
    (rowId: number, updatedConditions: definitions['RuleJson']['displayOptions'][0]['conditions']) => {
      const updatedRows = rows.map((row) => (row.id === rowId ? { ...row, conditions: updatedConditions } : row));
      setRows(updatedRows);
      updateLocalRuleJSON(updatedRows);

      // Update the parent form's ruleJSON state to keep it in sync
      const displayOptions = updatedRows.map(({ id, isNew, ...row }) => row);
      const updatedRuleJSON = {
        ...(localRuleJSON || { conditions: [] }),
        displayOptions,
      };
      onRuleJsonUpdate?.(updatedRuleJSON as definitions['RuleJson']);
    },
    [rows, updateLocalRuleJSON, localRuleJSON, onRuleJsonUpdate],
  );

  // Update global conditions
  const updateGlobalConditions = useCallback(
    (updatedConditions: definitions['RuleJson']['conditions']) => {
      const updatedRuleJSON: definitions['RuleJson'] = {
        ...(localRuleJSON || { displayOptions: [] }),
        displayOptionKey: localRuleJSON?.displayOptionKey || '',
        conditions: updatedConditions.map((c) => ({
          field: c.field,
          conditionType: c.conditionType,
          value: c.value,
        })),
      };
      setLocalRuleJSON(updatedRuleJSON as definitions['RuleJson']);

      // Update the parent form's ruleJSON state to keep it in sync
      onRuleJsonUpdate?.(updatedRuleJSON);
    },
    [localRuleJSON, onRuleJsonUpdate],
  );

  const validateRuleJSON = useCallback((ruleJSON: definitions['RuleJson']) => {
    const errors = [];

    ruleJSON.conditions.forEach((condition) => {
      const conditionConfig = promoMeta.promo_display_config_conditions.find(
        (config) => config.field === condition.field,
      );
      if (!conditionConfig) {
        errors.push('Condition field has not related promo_display_config_conditions config');
      }

      if (!conditionConfig?.conditionTypes.includes(condition.conditionType)) {
        errors.push(
          `Condition type is not valid for field ${condition.field}, only ${conditionConfig?.conditionTypes.join(
            ', ',
          )} are allowed`,
        );
      }

      if (condition.value.trim().length == 0) {
        errors.push(`Condition value is required for field ${condition.field}`);
      }
    });

    const duplicateDisplayOptionKeys = ruleJSON.displayOptions.filter(
      (displayOption, index, self) =>
        self.findIndex((t) => t.displayOptionKey === displayOption.displayOptionKey) !== index,
    );
    if (duplicateDisplayOptionKeys.length > 0) {
      errors.push(
        'Duplicate display option keys are not allowed (keys: ' +
          duplicateDisplayOptionKeys.map((displayOption) => displayOption.displayOptionKey).join(', ') +
          ')',
      );
    }

    const displayOptionsWithoutPromoCodeName = ruleJSON.displayOptions.filter(
      (displayOption) => displayOption.promoCodeName.length === 0,
    );
    if (displayOptionsWithoutPromoCodeName.length > 0) {
      errors.push('Set a promo code name for each display option');
    }

    return errors;
  }, []);

  const handleSaveRuleJSON = useCallback(async () => {
    if (!localRuleJSON) {
      enqueueSnackbar('No rules to save', { variant: 'warning' });
      return;
    }

    const errors = validateRuleJSON(localRuleJSON);
    if (errors.length > 0) {
      enqueueSnackbar(JSON.stringify(errors, null, 2), { variant: 'error' });
      return;
    }

    setIsSaving(true);
    try {
      await createOrUpdatePromoDisplayConfigRuleJson(promoDisplayConfigId, {
        rule_json: localRuleJSON,
      });

      enqueueSnackbar('Rules saved successfully', { variant: 'success', autoHideDuration: 800 });
      setLastSavedRuleJSON(localRuleJSON);
      onRuleJsonUpdate?.(localRuleJSON);

      // Reset all rows to mark them as existing (not new) after successful save
      setRows((prevRows) => prevRows.map((row) => ({ ...row, isNew: false })));
    } catch (error) {
      console.error(`Failed to save rule: ${error}`);
      enqueueSnackbar(`Failed to save rule: ${JSON.stringify(error, null, 2)}`, { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [localRuleJSON, promoDisplayConfigId, enqueueSnackbar, onRuleJsonUpdate]);

  return {
    // State
    localRuleJSON,
    rows,
    isSaving,
    hasUnsavedChanges,

    // Setters
    setRows,
    setLocalRuleJSON,
    setDisplayOptionKey,

    // Helpers
    updateLocalRuleJSON,
    updateRowConditions,
    updateGlobalConditions,
    handleSaveRuleJSON,
  };
};
